import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faRightFromBracket,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

import { UserAvatar } from '../UserAvatar';
import { capitalize } from '../../config/pricingConfig';
import { trackButtonClick } from '../../services/telemetry';

interface UserMenuProps {
  name?: string;
  /** Subscription tier label for the badge, or null while unknown. */
  tier?: string | null;
  onLogout: () => void;
}

/** Account dropdown opened from the nav avatar. */
export const UserMenu = ({ name, tier, onLogout }: UserMenuProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const rowClass = `
    flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm
    text-gray-700 transition-colors
    hover:bg-gray-50
  `;
  const rowLeftClass = 'flex items-center gap-2';
  const rowIconClass = 'w-4 text-gray-500';
  const chevronClass = 'text-xs text-gray-300';

  const close = () => setOpen(false);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className={`
          flex min-h-[44px] min-w-[44px] cursor-pointer items-center
          justify-center rounded-full transition-transform duration-100
          hover:opacity-90
          active:scale-95
        `}
      >
        <UserAvatar size={37} />
      </button>

      {open && (
        <div
          role="menu"
          className={`
            absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-100
            bg-white p-4 text-left shadow-[0px_4px_16px_rgba(0,0,0,0.12)]
          `}
        >
          {/* Identity + tier */}
          <div className="mb-3 flex flex-col items-center gap-2 text-center">
            <UserAvatar size={40} />
            {name && (
              <p
                className="max-w-full truncate text-sm text-gray-700"
                title={name}
                translate="no"
              >
                {name}
              </p>
            )}
            {tier && (
              <span
                className={`
                  inline-flex items-center gap-1 rounded border border-gray-200
                  px-2 py-0.5 text-xs text-gray-600
                `}
              >
                {capitalize(tier)}
              </span>
            )}
          </div>

          <div className="my-2 h-px bg-gray-100" />

          <Link
            to="/profile"
            role="menuitem"
            onClick={async () => {
              close();
              await trackButtonClick('NavBar', 'My Profile');
            }}
            className={rowClass}
          >
            <span className={rowLeftClass}>
              <FontAwesomeIcon icon={faUser} className={rowIconClass} />
              My Profile
            </span>
            <FontAwesomeIcon icon={faChevronRight} className={chevronClass} />
          </Link>

          <div className="my-2 h-px bg-gray-100" />

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onLogout();
            }}
            className={`${rowClass} w-full cursor-pointer`}
          >
            <span className={rowLeftClass}>
              <FontAwesomeIcon
                icon={faRightFromBracket}
                className={rowIconClass}
              />
              Log Out
            </span>
            <FontAwesomeIcon icon={faChevronRight} className={chevronClass} />
          </button>
        </div>
      )}
    </div>
  );
};
